class SeoSiteLinksBar {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('.seo-site-links-bar__viewport');
    this.list = root.querySelector('.seo-site-links-bar__list');
    this.prevButton = root.querySelector('.seo-site-links-bar__button--prev');
    this.nextButton = root.querySelector('.seo-site-links-bar__button--next');

    if (!this.viewport || !this.list || !this.prevButton || !this.nextButton) return;

    this.items = [];
    this.loopEnabled = false;
    this.scrollRaf = null;
    this.resizeTimeout = null;
    this.normalizeFallbackTimeout = null;

    this.onScroll = this.onScroll.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPrevClick = () => this.move(-1);
    this.onNextClick = () => this.move(1);

    this.prevButton.addEventListener('click', this.onPrevClick);
    this.nextButton.addEventListener('click', this.onNextClick);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

    if ('onscrollend' in window) {
      this.viewport.addEventListener('scrollend', this.onScrollEnd);
    }

    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.viewport);

    this.refresh();
  }

  refresh() {
    const preservedIndex = this.getActiveRealIndex();

    this.list.querySelectorAll('[data-seo-clone]').forEach((clone) => clone.remove());
    this.items = [...this.list.querySelectorAll('.seo-site-links-bar__item:not([data-seo-clone])')];

    if (this.isScrollable() && this.items.length > 1) {
      this.loopEnabled = true;
      this.root.classList.remove('seo-site-links-bar--static');
      this.prevButton.removeAttribute('disabled');
      this.nextButton.removeAttribute('disabled');
      this.buildLoop(preservedIndex);
      return;
    }

    this.loopEnabled = false;
    this.viewport.scrollLeft = 0;
    this.root.classList.add('seo-site-links-bar--static');
    this.prevButton.setAttribute('disabled', '');
    this.nextButton.setAttribute('disabled', '');
  }

  isScrollable() {
    return this.viewport.scrollWidth > this.viewport.clientWidth + 1;
  }

  buildLoop(preservedIndex = 0) {
    this.list.querySelectorAll('[data-seo-clone]').forEach((clone) => clone.remove());

    this.items.forEach((item) => {
      this.list.insertBefore(this.cloneItem(item), this.list.firstChild);
      this.list.appendChild(this.cloneItem(item));
    });

    requestAnimationFrame(() => {
      const target = this.items[preservedIndex] || this.items[0];
      this.viewport.scrollLeft = target ? target.offsetLeft : this.loopStart;
      this.normalize();
    });
  }

  cloneItem(item) {
    const clone = item.cloneNode(true);

    clone.setAttribute('data-seo-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));

    return clone;
  }

  get allItems() {
    return [...this.list.querySelectorAll('.seo-site-links-bar__item')];
  }

  get loopStart() {
    return this.items[0].offsetLeft;
  }

  get loopWidth() {
    const last = this.items[this.items.length - 1];
    return last.offsetLeft + last.offsetWidth - this.loopStart;
  }

  getActiveRealIndex() {
    if (this.items.length === 0) return 0;

    const scrollLeft = this.viewport.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  activeIndex() {
    const scrollLeft = this.viewport.scrollLeft;
    const items = this.allItems;
    const index = items.findIndex((item) => item.offsetLeft + item.offsetWidth > scrollLeft + 1);

    return index === -1 ? items.length - 1 : index;
  }

  onScroll() {
    if (!this.loopEnabled) return;

    if (this.scrollRaf) return;

    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.normalize();
    });
  }

  onScrollEnd() {
    if (this.loopEnabled) this.normalize();
  }

  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.refresh(), 150);
  }

  move(direction) {
    if (!this.loopEnabled) return;

    const items = this.allItems;
    const count = this.items.length;
    const scrollLeft = this.viewport.scrollLeft;
    const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    let index = this.activeIndex();
    let targetIndex = index + direction;

    if (direction > 0 && targetIndex >= items.length) {
      targetIndex = count * 2;
    }

    if (direction < 0 && targetIndex < 0) {
      targetIndex = count - 1;
    }

    let target = items[targetIndex];
    if (!target) return;

    let targetLeft = target.offsetLeft;

    if (direction > 0 && targetLeft <= scrollLeft + 1) {
      targetIndex = count * 2;
      target = items[targetIndex];
      targetLeft = target.offsetLeft;
    }

    if (direction < 0 && targetLeft >= scrollLeft - 1) {
      targetIndex = count - 1;
      target = items[targetIndex];
      targetLeft = target.offsetLeft;
    }

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    clearTimeout(this.normalizeFallbackTimeout);
    this.viewport.scrollTo({ left: Math.min(targetLeft, maxScroll), behavior });

    if ('onscrollend' in window) return;

    this.normalizeFallbackTimeout = setTimeout(() => this.normalize(), behavior === 'auto' ? 0 : 350);
  }

  normalize() {
    if (!this.loopEnabled) return;

    const start = this.loopStart;
    const width = this.loopWidth;
    let scrollLeft = this.viewport.scrollLeft;

    if (scrollLeft >= start + width) {
      scrollLeft -= width;
    } else if (scrollLeft < start) {
      scrollLeft += width;
    } else {
      return;
    }

    this.viewport.scrollLeft = scrollLeft;
  }
}

function initSeoSiteLinksBars(scope = document) {
  scope.querySelectorAll('[data-seo-site-links-bar]:not([data-seo-initialized])').forEach((root) => {
    root.dataset.seoInitialized = 'true';
    new SeoSiteLinksBar(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initSeoSiteLinksBars());
} else {
  initSeoSiteLinksBars();
}

document.addEventListener('shopify:section:load', (event) => {
  initSeoSiteLinksBars(event.target);
});
